import React, { Suspense, useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import ReactPaginate from "react-paginate"
import { ArrowBigLeftDashIcon, ArrowBigRightDashIcon } from "lucide-react"
import Spinner from "../Spinner"
import { fetchOrders, deleteOrder } from "../../store"
import { Modal, ModalHeader, ModalActions } from "../ui/Modal"
import { Link } from "react-router-dom"
import { formatCurrency } from "../../util"
import "../styles/AdminOrdersPage.css"

function AdminOrdersPage() {
  const dispatch = useDispatch()
  const { orders } = useSelector((state) => state.orders)
  const [itemsPerPage, setItemsPerPage] = useState(24)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [itemOffset, setItemOffset] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const endOffset = itemOffset + itemsPerPage

  useEffect(() => {
    dispatch(fetchOrders())
  }, [dispatch])

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % orders.length
    setItemOffset(newOffset)
    window.scrollTo(0, 0)
  }

  const handleDetailsClick = (order) => {
    setSelectedOrder(order)
  }

  const handleDeleteClick = (orderId) => {
    if (window.confirm("Are you sure you want to --DELETE-- this order?")) {
      dispatch(deleteOrder(orderId))
        .then(() => {
          console.log("Order Deleted Successfully")
          dispatch(fetchOrders())
        })
        .catch((error) => {
          console.error("Error Deleting Order:", error)
        })
    }
  }

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase())
  }

  const filteredOrders = orders.filter((order) => {
    const fullName = `${order.lastName}, ${order.firstName}`
    const shippingAddress = `${order.street}, ${order.city}, ${order.state} ${order.zip}`
    return (
      fullName.toLowerCase().includes(searchTerm) ||
      shippingAddress.toLowerCase().includes(searchTerm)
    )
  })

  const currentOrders =
    filteredOrders.length > 0 ? filteredOrders.slice(itemOffset, endOffset) : []
  const pageCount = Math.ceil(filteredOrders.length / itemsPerPage)

  return (
    <div className="admin-orders-page-container">
      {selectedOrder && (
        <Modal
          open={selectedOrder}
          className="admin-modal"
          responsive
          onClickBackdrop={() => setSelectedOrder(null)}
        >
          <ModalHeader className="admin-modal-header">
            {selectedOrder &&
              `${selectedOrder.lastName}, ${selectedOrder.firstName}'s Order Details`}
          </ModalHeader>
          <div className="admin-modal-content">
            <div className="admin-modal-content-section">
              <div className="admin-modal-content-details">Total Items:</div>
              <div className="admin-modal-content-details">Total Price:</div>
              <div className="admin-modal-content-details">Order Date:</div>
              <div className="admin-modal-content-details">Shipped To:</div>
            </div>
            <div className="admin-modal-content-section">
              <div className="admin-modal-content-detail">
                {selectedOrder.lineItems.reduce(
                  (acc, item) => acc + item.quantity,
                  0
                )}
              </div>
              <div className="admin-modal-content-detail">
                {formatCurrency(
                  selectedOrder.lineItems.reduce(
                    (acc, item) => acc + item.quantity * item.product.price,
                    0
                  )
                )}
              </div>
              <div className="admin-modal-content-detail">
                {new Date(selectedOrder.createdAt).toLocaleString()}
              </div>
              <div className="admin-modal-content-detail">
                <div className="admin-modal-address">
                  <div>
                    {`${selectedOrder.lastName}, ${selectedOrder.firstName}`}
                  </div>
                  <div>
                    {selectedOrder.street}, {selectedOrder.city},{" "}
                    {selectedOrder.state}, {selectedOrder.zip}
                  </div>
                </div>
              </div>
            </div>
            <div className="admin-modal-items">
              <div className="admin-modal-content-detail">Items:</div>
              {selectedOrder.lineItems.map((item) => (
                <div className="admin-modal-item" key={item.id}>
                  <Suspense fallback={<Spinner />}>
                    <div className="admin-modal-item-avatar">
                      <img
                        src={item.product.imageURL}
                        alt={item.product.name}
                      />
                    </div>
                  </Suspense>
                  <div className="admin-modal-item-name">
                    <Link to={`/products/${item.product.id}`}>
                      {item.product.name}
                    </Link>
                  </div>
                  <div className="admin-modal-item-quantity">
                    {item.quantity}
                  </div>
                </div>
              ))}
            </div>
            <ModalActions />
          </div>
        </Modal>
      )}
      <div className="admin-table-container">
        <h1 className="admin-table-heading">Orders</h1>
        <div className="admin-table-search">
          <div className="admin-search-control">
            <label className="admin-search-label">Search</label>
            <input
              type="text"
              placeholder="Search Orders"
              onChange={handleSearch}
              className="admin-search-input"
            />
          </div>
        </div>
        <div className="admin-table-wrapper">
          <table className="admin-orders-table">
            <thead>
              <tr className="admin-table-row">
                <th className="admin-orders-table-header">User</th>
                <th className="admin-orders-table-header">Shipping Address</th>
                <th className="admin-orders-table-header">Order Date</th>
                <th className="admin-orders-table-header">Updated At</th>
                <th className="admin-orders-table-header">Total Items</th>
                <th className="admin-orders-table-header">Total Price</th>
                <th className="admin-orders-table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.length > 0 ? (
                currentOrders.map((order) => {
                  const address = `${order.street}, ${order.city}, ${order.state} ${order.zip}`
                  const totalItems = order.lineItems.reduce(
                    (acc, item) => acc + item.quantity,
                    0
                  )
                  const totalPrice = order.lineItems.reduce(
                    (acc, item) => acc + item.quantity * item.product.price,
                    0
                  )
                  return (
                    <tr
                      key={order.id}
                      className={`admin-table-row ${
                        selectedOrder &&
                        selectedOrder.id === order.id &&
                        "admin-table-row-selected"
                      }`}
                    >
                      <td className="admin-table-cell">
                        <div className="admin-table-cell-avatar">
                          <img
                            src={order.user.avatar}
                            alt={order.user.username}
                            loading="lazy"
                          />
                        </div>
                        <div className="admin-orders-table-cell-username">
                          {`${order.lastName}, ${order.firstName}`}
                        </div>
                      </td>
                      <td className="admin-orders-table-cell">{address}</td>
                      <td className="admin-orders-table-cell">
                        {new Date(order.createdAt).toLocaleString()}
                      </td>
                      <td className="admin-orders-table-cell">
                        {new Date(order.updatedAt).toLocaleString()}
                      </td>
                      <td className="admin-orders-table-cell">{totalItems}</td>
                      <td className="admin-orders-total-price-cell">
                        {formatCurrency(totalPrice)}
                      </td>
                      <th className="admin-orders-action-cell">
                        <button
                          className="admin-orders-table-action-button"
                          onClick={() => handleDetailsClick(order)}
                        >
                          Details
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteClick(order.id)}
                        >
                          <svg className="delete-svgIcon" viewBox="0 0 448 512">
                            <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"></path>
                          </svg>
                        </button>
                      </th>
                    </tr>
                  )
                })
              ) : (
                <Spinner />
              )}
            </tbody>
          </table>
          <ReactPaginate
            breakLabel="..."
            nextLabel={<ArrowBigRightDashIcon size={24} alt="Next" />}
            onPageChange={handlePageClick}
            pageRangeDisplayed={5}
            pageCount={pageCount}
            previousLabel={<ArrowBigLeftDashIcon size={24} alt="Previous" />}
            renderOnZeroPageCount={null}
            className="admin-pagination"
            activeClassName="admin-pagination-active"
            pageClassName="admin-pagination-page"
            previousClassName="admin-pagination-previous"
            nextClassName="admin-pagination-next"
          />
        </div>
      </div>
    </div>
  )
}

export default AdminOrdersPage
